import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FeatureFlag {
  readonly featureCode: string;
  readonly description: string | null;
  readonly enabled: boolean;
}

/**
 * M22 L22.05 — Mobile Feature Flags.
 * Reconciled to the canonical feature-toggle registry (/v1/features) rather
 * than a new app_feature_flags table — the mobile shell honours the same flags
 * the platform defines, so there is a single source of truth (no duplicate tables).
 * Toggling writes to the guarded PUT /v1/features registry (company scope).
 */
export function M22FeatureFlagsPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<FeatureFlag[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    return apiRequest<{ items: FeatureFlag[] }>('/v1/features', { token })
      .then((r) => setRows(r.items))
      .catch((e) => setError((e as Error).message));
  }, [token]);

  useEffect(() => {
    let alive = true;
    apiRequest<{ items: FeatureFlag[] }>('/v1/features', { token })
      .then((r) => { if (alive) setRows(r.items); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const toggle = useCallback(
    async (f: FeatureFlag) => {
      setError(null);
      setNotice(null);
      setBusy(f.featureCode);
      try {
        await apiRequest('/v1/features', {
          method: 'PUT',
          token,
          body: { featureCode: f.featureCode, scope: 'COMPANY', enabled: !f.enabled },
        });
        setNotice(t('m22.featureFlags.toggleNotice', { code: f.featureCode, state: !f.enabled ? t('m22.featureFlags.stateOn') : t('m22.featureFlags.stateOff') }));
        await load();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [token, load],
  );

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m22.featureFlags.loading')}</p>;
  return (
    <main aria-label="mobile-feature-flags">
      <h1>{t('m22.featureFlags.title')}</h1>
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {rows.length === 0 ? (
        <p>{t('m22.featureFlags.empty')}</p>
      ) : (
        <table aria-label="mobile-feature-flags-table">
          <thead>
            <tr><th>{t('m22.featureFlags.colCode')}</th><th>{t('m22.featureFlags.colName')}</th><th>{t('m22.featureFlags.colState')}</th><th>{t('m22.featureFlags.colAction')}</th></tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.featureCode}>
                <td>{f.featureCode}</td>
                <td>{f.description}</td>
                <td>{f.enabled ? t('m22.featureFlags.stateOn') : t('m22.featureFlags.stateOff')}</td>
                <td>
                  <button
                    type="button"
                    aria-label={`toggle-${f.featureCode}`}
                    disabled={busy !== null}
                    onClick={() => toggle(f)}
                  >
                    {f.enabled ? t('m22.featureFlags.disable') : t('m22.featureFlags.enable')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
